import React, { useState, useEffect } from "react";
import {
  TextBoxComponent,
  TextAreaComponent,
} from "@syncfusion/ej2-react-inputs";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";
import {
  CheckBoxSelection,
  MultiSelectComponent,
} from "@syncfusion/ej2-react-dropdowns";
import { Inject } from "@syncfusion/ej2-react-grids";

const DeclareWizardModal = ({
  isOpen,
  onClose,
  onSubmit,
  centerState,
  centerCounty,
  userData,
  userName,
}) => {
  const initialState = {
    disaster_name: "",
    description: "",
    disaster_type: [],
    disaster_stage: "Response",
    disaster_status: "declared",
    disaster_state: centerState,
    affected_counties: centerCounty,
    disaster_level: [],
    disaster_impact: "",
    declared_by: userName,
    approved_by: "",
    date_declared: new Date(),
    disaster_date: new Date(),
    approved_date: "",
    is_deleted: false,
    center_id: userData.center_id,
  };

  const [disasterData, setDisasterData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    setDisasterData((prev) => ({
      ...prev,
      disaster_state: centerState,
      affected_counties: centerCounty,
    }));
  }, [centerState, centerCounty]);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "disaster_name",
      "disaster_type",
      "description",
      "disaster_level",
      "disaster_impact",
      "disaster_date",
    ];

    requiredFields.forEach((field) => {
      if (
        !disasterData[field] ||
        (Array.isArray(disasterData[field]) && disasterData[field].length === 0)
      ) {
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setDisasterData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: undefined,
      }));
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSubmit = () => {
    setShowConfirmModal(false);
    onSubmit(disasterData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="w-[1000px] rounded-t-lg bg-gradient-to-r from-red-700 to-red-600 p-4 shadow-lg">
          <div className="flex w-full items-center justify-between">
            <div className="flex w-full items-center">
              <span className="-mt-0.5 mr-2 animate-pulse text-2xl">⚠️</span>
              <div className="flex w-full justify-between">
                <p className="default-font text-2xl font-bold tracking-wide text-white">
                  Declare a Disaster
                </p>
                <p className="default-font text-2xl font-bold tracking-wide text-white">
                  All information can be updated at a later time if needed.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="w-[1000px] rounded-b-lg bg-white p-4 shadow-xl">
          <form onSubmit={handleFormSubmit}>
            {/* Basic Disaster Info */}
            <div className="mb-2 grid grid-cols-2 gap-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Disaster Name
                </label>
                <TextBoxComponent
                  value={disasterData.disaster_name}
                  change={(e) => handleChange("disaster_name", e.value)}
                  placeholder="Enter disaster name"
                  required
                  cssClass="e-outline border-red-200 hover:border-red-400"
                />
                {errors.disaster_name && (
                  <p className="text-sm text-red-500">{errors.disaster_name}</p>
                )}
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Type of Disaster
                </label>
                <MultiSelectComponent
                  dataSource={[
                    "Fire",
                    "Flood",
                    "Tornado",
                    "Hurricane",
                    "Earthquake",
                    "Volcano",
                  ]}
                  placeholder="Select disaster type"
                  value={disasterData.disaster_type}
                  change={(e) => handleChange("disaster_type", e.value)}
                  cssClass="e-outline border-red-200 hover:border-red-400"
                  mode="Box"
                  showSelectAll={true}
                  required
                />
                {errors.disaster_type && (
                  <p className="text-sm text-red-500">{errors.disaster_type}</p>
                )}
              </div>
            </div>
            {/* Description */}
            <div className="mb-2 w-full">
              <label className="mb-2 block text-sm font-bold text-gray-800">
                Description
              </label>
              <div className="relative w-full">
                <TextAreaComponent
                  value={disasterData.description}
                  change={(e) => handleChange("description", e.value)}
                  placeholder="Enter disaster description"
                  required
                  cssClass="e-outline border-red-200 hover:border-red-400"
                  width="100%"
                  height="auto"
                  multiline={true}
                  resizeMode="Vertical"
                />
                {errors.description && (
                  <p className="text-sm text-red-500">{errors.description}</p>
                )}
              </div>
            </div>
            <div className="mb-2 grid grid-cols-2 gap-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Affected States
                </label>
                <MultiSelectComponent
                  dataSource={centerState}
                  placeholder="Select affected states"
                  value={disasterData.disaster_state}
                  change={(e) => handleChange("disaster_state", e.value)}
                  selectAllText="Select All"
                  unSelectAllText="Unselect All"
                  showSelectAll={true}
                  cssClass="e-outline border-red-200 hover:border-red-400 [&_.e-dropdownbase]:ml-[10px]"
                  mode="Box"
                  enabled={false}
                  required
                />
                {errors.disaster_state && (
                  <p className="text-sm text-red-500">
                    {errors.disaster_state}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Affected Counties
                </label>
                <MultiSelectComponent
                  dataSource={centerCounty}
                  placeholder="Select affected counties"
                  value={disasterData.affected_counties}
                  change={(e) => handleChange("affected_counties", e.value)}
                  selectAllText="Select All"
                  unSelectAllText="Unselect All"
                  showSelectAll={true}
                  enabled={false}
                  cssClass="e-outline border-red-200 hover:border-red-400 [&_.e-dropdownbase]:ml-[10px]"
                  required
                >
                  <Inject services={[CheckBoxSelection]} />
                </MultiSelectComponent>
                {errors.affected_counties && (
                  <p className="text-sm text-red-500">
                    {errors.affected_counties}
                  </p>
                )}
              </div>
            </div>

            {/* Declaration Level */}
            <div className="mb-2 grid grid-cols-2 gap-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Disaster impact level
                </label>
                <DropDownListComponent
                  dataSource={["Minor", "Moderate", "Major", "Catastrophic"]}
                  placeholder="Select disaster impact level"
                  value={disasterData.disaster_impact}
                  change={(e) => handleChange("disaster_impact", e.value)}
                  cssClass="e-outline border-red-200 hover:border-red-400"
                  required
                />
                {errors.disaster_impact && (
                  <p className="text-sm text-red-500">
                    {errors.disaster_impact}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Disaster declaration level
                </label>
                <MultiSelectComponent
                  dataSource={["Local", "State", "Federal"]}
                  placeholder="Select Disaster declaration level"
                  value={disasterData.disaster_level}
                  change={(e) => handleChange("disaster_level", e.value)}
                  showSelectAll={true}
                  allowFiltering={true}
                  mode="Box"
                  cssClass="e-outline border-red-200 hover:border-red-400 [&_.e-dropdownbase]:ml-[10px]"
                />
                {errors.disaster_level && (
                  <p className="text-sm text-red-500">
                    {errors.disaster_level}
                  </p>
                )}
              </div>
            </div>
            <div className="mb-2 grid grid-cols-2 gap-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-800">
                  Date of the Disaster
                </label>
                <DatePickerComponent
                  name="disaster_date"
                  placeholder="Select disaster impact level"
                  value={disasterData.disaster_date}
                  change={(e) => handleChange("disaster_date", e.value)}
                  cssClass="e-outline border-red-200 hover:border-red-400"
                  required
                />
              </div>
              <div className="flex h-full w-full place-items-end justify-end gap-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-cancel mr-3 px-4 py-2 hover:cursor-pointer"
                >
                  Cancel
                </button>
                <div
                  type="submit"
                  onClick={handleFormSubmit}
                  className="btn animate-[pulse_2s_ease-in-out_infinite] rounded-md bg-gradient-to-r from-red-700 to-red-600 px-8 py-2 font-bold text-white shadow-lg transition-all hover:cursor-pointer hover:from-red-800 hover:to-red-700 hover:shadow-red-400/50"
                >
                  Declare Disaster
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showConfirmModal && (
        <div className="bg-gray-900 fixed inset-0 flex items-center justify-center bg-opacity-50">
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center ">
            <div className="flex w-[440px] justify-between rounded-t-lg bg-gradient-to-r from-red-700 to-red-600 p-4 ">
              <p className="font-semibold text-white">Declare Disaster</p>
              <div
                className="cursor-pointer"
                onClick={() => setShowConfirmModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_9081_292"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                  >
                    <rect width="24" height="24" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_9081_292)">
                    <circle
                      cx="7"
                      cy="7"
                      r="7"
                      transform="matrix(-1 0 0 1 20 5)"
                      fill="white"
                    />
                    <path
                      d="M12 13.4L14.9 16.3C15.0833 16.4833 15.3167 16.575 15.6 16.575C15.8833 16.575 16.1167 16.4833 16.3 16.3C16.4833 16.1167 16.575 15.8833 16.575 15.6C16.575 15.3167 16.4833 15.0833 16.3 14.9L13.4 12L16.3 9.1C16.4833 8.91667 16.575 8.68333 16.575 8.4C16.575 8.11667 16.4833 7.88333 16.3 7.7C16.1167 7.51667 15.8833 7.425 15.6 7.425C15.3167 7.425 15.0833 7.51667 14.9 7.7L12 10.6L9.1 7.7C8.91667 7.51667 8.68333 7.425 8.4 7.425C8.11667 7.425 7.88333 7.51667 7.7 7.7C7.51667 7.88333 7.425 8.11667 7.425 8.4C7.425 8.68333 7.51667 8.91667 7.7 9.1L10.6 12L7.7 14.9C7.51667 15.0833 7.425 15.3167 7.425 15.6C7.425 15.8833 7.51667 16.1167 7.7 16.3C7.88333 16.4833 8.11667 16.575 8.4 16.575C8.68333 16.575 8.91667 16.4833 9.1 16.3L12 13.4ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22Z"
                      fill="#CE2222"
                    />
                  </g>
                </svg>
              </div>
            </div>
            <div className="w-[440px] rounded-b-lg bg-white p-6 text-center shadow-lg">
              <div
                className="flex items-center justify-center"
                style={{
                  width: "80px",
                  height: "80px",
                  padding: "16px",
                  borderRadius: "1000px",
                  background: "#FFD182",
                  margin: "0 auto",
                }}
              >
                <svg
                  width="81"
                  height="80"
                  viewBox="0 0 81 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_9136_392"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="81"
                    height="80"
                  >
                    <rect x="0.5" width="80" height="80" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_9136_392)">
                    <circle cx="41" cy="40" r="29" fill="#242424" />
                    <path
                      d="M40.3327 60.0003C41.4993 60.0003 42.4855 59.5975 43.291 58.792C44.0966 57.9864 44.4993 57.0003 44.4993 55.8337C44.4993 54.667 44.0966 53.6809 43.291 52.8753C42.4855 52.0698 41.4993 51.667 40.3327 51.667C39.166 51.667 38.1799 52.0698 37.3743 52.8753C36.5688 53.6809 36.166 54.667 36.166 55.8337C36.166 57.0003 36.5688 57.9864 37.3743 58.792C38.1799 59.5975 39.166 60.0003 40.3327 60.0003ZM40.4993 73.3337C35.8882 73.3337 31.5549 72.4587 27.4993 70.7087C23.4438 68.9587 19.916 66.5837 16.916 63.5837C13.916 60.5837 11.541 57.0559 9.79102 53.0003C8.04102 48.9448 7.16602 44.6114 7.16602 40.0003C7.16602 35.3892 8.04102 31.0559 9.79102 27.0003C11.541 22.9448 13.916 19.417 16.916 16.417C19.916 13.417 23.4438 11.042 27.4993 9.29199C31.5549 7.54199 35.8882 6.66699 40.4993 6.66699C45.1105 6.66699 49.4438 7.54199 53.4993 9.29199C57.5549 11.042 61.0827 13.417 64.0827 16.417C67.0827 19.417 69.4577 22.9448 71.2077 27.0003C72.9577 31.0559 73.8327 35.3892 73.8327 40.0003C73.8327 44.6114 72.9577 48.9448 71.2077 53.0003C69.4577 57.0559 67.0827 60.5837 64.0827 63.5837C61.0827 66.5837 57.5549 68.9587 53.4993 70.7087C49.4438 72.4587 45.1105 73.3337 40.4993 73.3337ZM40.8327 25.667C42.2216 25.667 43.3882 25.1809 44.3327 24.2087C45.2771 23.2364 45.7493 22.0559 45.7493 20.667C45.7493 19.2781 45.2771 18.1114 44.3327 17.167C43.3882 16.2225 42.2216 15.7503 40.8327 15.7503C39.4438 15.7503 38.2771 16.2225 37.3327 17.167C36.3882 18.1114 35.916 19.2781 35.916 20.667C35.916 22.0559 36.3882 23.2364 37.3327 24.2087C38.2771 25.1809 39.4438 25.667 40.8327 25.667ZM40.4993 46.667C41.5549 46.667 42.4577 46.3059 43.2077 45.5837C43.9577 44.8614 44.3327 43.9725 44.3327 42.917C44.3327 42.1392 44.0966 41.4448 43.6243 40.8337L48.166 36.2503L46.4993 34.5837L40.4993 40.5837L34.4993 34.5837L32.8327 36.2503L37.3743 40.8337C36.9021 41.4448 36.666 42.1392 36.666 42.917C36.666 43.9725 37.041 44.8614 37.791 45.5837C38.541 46.3059 39.4438 46.667 40.4993 46.667Z"
                      fill="white"
                    />
                  </g>
                </svg>
              </div>
              <p className="text-lg mt-4 font-semibold">
                Are you sure you want to declare this disaster?
              </p>
              <p className="mt-2 text-sm text-gray-500">
                This action will notify all relevant authorities.
              </p>
              <div className="mt-4 flex justify-between gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="btn-cancel flex-1 rounded p-4 text-white hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  className="btn-default flex-1 rounded p-4 text-white hover:bg-blue-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeclareWizardModal;
